// Canonical ecommerce event layer — emit once, fan out everywhere.
// Browser: dataLayer (GA4/GTM), Meta fbq — only with consent.
// Server: purchase/lead/sign_up POSTed to /api/events with a shared event_id so
// browser and server events deduplicate. PII is SHA-256 hashed before sending.
import api from "@/lib/api";

const CONSENT_KEY = "siyaara_consent";

export function getConsent() {
  try { return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null"); } catch { return null; }
}

export function setConsent(c) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...c, ts: new Date().toISOString() }));
  applyConsentMode(c);
}

/** Google Consent Mode v2 — everything denied until the customer chooses. */
export function applyConsentMode(c) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const granted = (b) => (b ? "granted" : "denied");
  window.gtag("consent", c ? "update" : "default", {
    analytics_storage: granted(c?.analytics),
    ad_storage: granted(c?.marketing),
    ad_user_data: granted(c?.marketing),
    ad_personalization: granted(c?.marketing),
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export async function hashPII(value) {
  if (!value) return "";
  const norm = String(value).trim().toLowerCase();
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(norm));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const META_MAP = {
  page_view: "PageView", view_item: "ViewContent", add_to_cart: "AddToCart",
  add_to_wishlist: "AddToWishlist", begin_checkout: "InitiateCheckout",
  purchase: "Purchase", search: "Search", sign_up: "CompleteRegistration",
  lead: "Lead", subscribe: "Subscribe", view_item_list: "ViewContent",
};

const SERVER_EVENTS = new Set(["purchase", "lead", "sign_up", "add_payment_info", "refund"]);

const ANON_KEY = "siyaara_anon";
export function getAnonId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

/** First-party affinity signal (like/dislike/wishlist/view) → CRM taste profile. */
export async function trackAffinity(product, signal) {
  try {
    await api.post("/affinity", {
      product_id: product.id || product.product_id || "",
      signal,
      anon_id: getAnonId(),
      title: product.title || "",
      metal: product.metal || "",
      purity: product.purity || "",
      category: product.category || product.product_type || "",
    });
  } catch { /* never block the shopper */ }
}

let snapTimer = null;
/** Debounced server-side cart snapshot → powers abandoned-cart journeys. */
export function snapshotCart(items, subtotal) {
  clearTimeout(snapTimer);
  snapTimer = setTimeout(() => {
    api.post("/cart/snapshot", {
      anon_id: getAnonId(),
      subtotal,
      items: items.map((i) => ({ title: i.title, qty: i.qty, price: i.locked_price, sku: i.sku || "" })),
    }).catch(() => {});
  }, 1200);
}

/** Emit one canonical event. params carries items: [{item_id, item_name, category, metal, purity, price, quantity}], value, currency. */
export async function emit(event, params = {}, user = {}) {
  const consent = getConsent();
  const event_id = `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const payload = { ...params, event_id, currency: params.currency || "INR" };

  // GA4 / GTM — analytics consent
  if (consent?.analytics) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }
  // Meta pixel — marketing consent
  if (consent?.marketing && window.fbq && META_MAP[event]) {
    try {
      window.fbq("track", META_MAP[event], { ...payload, content_ids: (params.items || []).map((i) => i.item_id) }, { eventID: event_id });
    } catch { /* pixel blocked */ }
  }
  // Server-side (dedup via shared event_id); PII hashed here, never raw
  if (SERVER_EVENTS.has(event)) {
    const body = {
      event, event_id, params: payload,
      user: {
        em: user.email ? await hashPII(user.email) : "",
        ph: user.phone ? await hashPII(user.phone) : "",
      },
      consent: { analytics: !!consent?.analytics, marketing: !!consent?.marketing, ts: consent?.ts || "" },
      page: window.location.pathname,
    };
    try {
      if (event === "purchase" && navigator.sendBeacon) {
        navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/events`, JSON.stringify(body));
      } else {
        await api.post("/events", body);
      }
    } catch { /* never block the shopper on tracking */ }
  }
  return event_id;
}
